<i18n>
{
	"en": {
		"autoSearch": "Autosearch every 60 seconds",
		"customer": "Customer",
		"fromDespatchParty": "From Location",
		"goodsTypes": "Goods Type",
		"mainDesc": "You can save search settings as a quick search for use in the menu. Select the search criteria to save in the search:",
		"makePublic": "Make this quick search available to other users",
		"orderTypes": "Order Type",
		"save": "Save",
		"searchCriteria": "Search Criteria",
		"searchCriteriaDesc": "Select the criteria you want the shortcut to use:",
		"searchName": "Name",
		"searchNameDesc": "Name of the search displayed in the menu",
		"timeInterval": "Time Interval",
		"timeIntervalAfter": "Today | day after present | days after present",
		"timeIntervalBefore": "Today | day before present | days before present",
		"timeIntervalDesc": "The search will use this time interval by default",
		"title": "Save Quick Search",
		"toDeliveryParty": "To Location",
		"viewMainMenu": "Show this quick search in the main menu",
		"viewMainMenuDesc": "It is also possible to change this later",
		"cancelLabel": "Cancel",
		"failedToSave": "Failed to save search",
		"failedToDelete": "Failed to delete search",
		"savedSearch": "Quicksearch saved",
		"deletedSearch": "Quicksearch deleted",
		"pickupStatus": "Pickup Status",
		"deliveryStatus": "Delivery Status",
		"economicalStatus": "Economical Status",
		"references": "References",
		"before": "Before",
		"after": "After",
		"orderStatus": "Operational Status",
		"noDaySet": "No day set",
		"waybillNumber": "Waybill Number",
		"noLabel": "Please give this quick search a name",
		"emptyPatch": "Nothing to update",
		"inboundEdi": "Inbound EDI",
		"deviation": "Deviation"
	},
	"no": {
		"autoSearch": "Autosøk hvert 60. sekund",
		"customer": "Kunde",
		"fromDespatchParty": "Fra sted",
		"goodsTypes": "Godstype",
		"mainDesc": "Du kan lagre søkeinstillingene som en hurtigsøk for å gjøre dette tilgjengelig i menyen. Velg hvilke av søkekriteriene som skal lagres i hurtigsøket:",
		"makePublic": "Gjøre hurtigsøket tilgjengelig for andre brukere",
		"orderTypes": "Oppdragstype",
		"save": "Lagre",
		"searchCriteria": "Søkekriterier",
		"searchCriteriaDesc": "Velg de kriteriene du vil at hurtigsøket skal bruke:",
		"searchName": "Navn",
		"searchNameDesc": "Navnet på hurtigsøket vises i menyen",
		"timeInterval": "Tidsintervall",
		"timeIntervalAfter": "I dag | dag etter nådato | dager etter nådato",
		"timeIntervalBefore": "I dag | dag før nådato | dager før nådato",
		"timeIntervalDesc": "Søket vil bruke dette tidsintervallet som standard",
		"title": "Lagre hurtigsøk",
		"toDeliveryParty": "Til sted",
		"viewMainMenu": "Vis dette hurtigsøket i hovedmenyen",
		"viewMainMenuDesc": "Det er også mulig å endre dette senere",
		"cancelLabel": "Avbryt",
		"failedToSave": "Kunne ikke lagre søket",
		"failedToDelete": "Kunne ikke slette søket",
		"savedSearch": "Hurtigsøk lagret",
		"deletedSearch": "Hurtigsøk slettet",
		"pickupStatus": "Hentestatus",
		"deliveryStatus": "Leveringsstatus",
		"economicalStatus": "Økonomisk status",
		"references": "Referanser",
		"before": "Før",
		"after": "Etter",
		"orderStatus": "Operativ status",
		"noDaySet": "Ingen dag satt",
		"waybillNumber": "Fraktbrevnr",
		"noLabel": "Vennligst gi hurtigsøket et navn",
		"emptyPatch": "Ingen endring gjort",
		"inboundEdi": "Inngående EDI",
		"deviation": "Avvik"
	}
}
</i18n>

<template>
	<t-dialog
		:isShown="isShown"
		:title="$t('title')"
		:confirmLabel="$t('save')"
		:cancelLabel="$t('cancelLabel')"
		disableCloseOnConfirm
		width="650px"
		@close="onClose"
		@ok="onSaveSearch"
		@shown="processShown"
	>
		<div class="save-search-top">
			<div style="padding-bottom:10px">{{ $t('mainDesc') }}</div>
			<t-text-input
				v-model="newSettings.label"
				:label="$t('searchName')"
				:description="$t('searchNameDesc')"
				width="100%"
				:isRequired="true"
				:isInvalid="missingLabel && !newSettings.label"
				:validationMessage="$t('noLabel')"
			/>
		</div>

		<div class="save-search-mid">

			<OrderFilters :orderCriteria="orderCriteria" :orderFilter="newSettings"/>

			<div class="center">
				<div class="search-components">
					<div class="order-search">
						<div class="save-search-time">
							<t-input-wrapper :label="$t('timeInterval')" :description="$t('timeIntervalDesc')"/>
							<div class="time-interval">
								<t-number-input
									v-model="before"
									:placeholder="$t('noDaySet')"
									:extraDisplayText="$tc(fromDateIsAfterToday ? 'timeIntervalAfter' : 'timeIntervalBefore', parseInt(before))"
									min="0"
									hideZeroValue
									leftAlign
									style="grid-area: input1"
								/>
								<t-switch
									v-model="fromDateIsAfterToday"
									:labelBefore="$t('before')"
									:label="$t('after')"
									:noColor="true"
									style="grid-area: switch1"
								/>
								<t-checkbox
									v-model="orderTimeInterval.useAfter"
									style="grid-area: checkbox"
								/>
								<t-number-input
									v-model="after"
									:placeholder="$t('noDaySet')"
									:extraDisplayText="$tc(toDateIsAfterToday ? 'timeIntervalAfter' : 'timeIntervalBefore', parseInt(after))"
									min="0"
									hideZeroValue
									leftAlign
									style="grid-area: input2"
								/>
								<t-switch
									v-model="toDateIsAfterToday"
									:labelBefore="$t('before')"
									:label="$t('after')"
									:noColor="true"
									style="grid-area: switch2"
								/>
							</div>

							<t-switch
								v-model="newSettings.autoSearch"
								:label="$t('autoSearch')"
							/>
						</div>
						<div class="save-search-criteria">
							<div class="search-criteria-label">{{ $t('searchCriteria') }}</div>
							<div class="search-criteria-description">{{ $t('searchCriteriaDesc') }}</div>

							<div style="display: grid; grid-row-gap: 10px; grid-template-rows: repeat(4, min-content);">
								<t-switch
									v-model="orderCriteria.fromDespatchParty"
									:label="$t('fromDespatchParty')"
									@change="updateOrderFilter('fromDespatchParty', $event)"
								/>
								<t-switch
									v-model="orderCriteria.toDeliveryParty"
									:label="$t('toDeliveryParty')"
									@change="updateOrderFilter('toDeliveryParty', $event)"
								/>
								<t-switch
									v-model="orderCriteria.customer"
									:label="$t('customer')"
									@change="updateOrderFilter('customer', $event)"
								/>
								<t-switch
									v-model="orderCriteria.goodsTypes"
									:label="$t('goodsTypes')"
									@change="updateOrderFilter('goodsTypes', $event)"
								/>
								<t-switch
									v-model="orderCriteria.orderTypes"
									:label="$t('orderTypes')"
									@change="updateOrderFilter('orderTypes', $event)"
								/>
								<t-switch
									v-model="orderCriteria.orderStatus"
									:label="$t('orderStatus')"
									@change="updateOrderFilter('orderStatus', $event)"
								/>
								<t-switch
									v-model="orderCriteria.hasAtd"
									:label="$t('pickupStatus')"
									@change="updateHasStatus('hasAtd', $event)"
								/>
								<t-switch
									v-model="orderCriteria.hasAta"
									:label="$t('deliveryStatus')"
									@change="updateHasStatus('hasAta', $event)"
								/>
								<t-switch
									v-model="orderCriteria.economicalStatus"
									:label="$t('economicalStatus')"
									@change="updateOrderFilter('economicalStatus', $event)"
								/>
								<t-switch
									v-model="orderCriteria.references"
									:label="$t('references')"
									@change="updateOrderReferences('references', $event)"
								/>
								<t-switch
									v-model="orderCriteria.waybillNumber"
									:label="$t('waybillNumber')"
									@change="updateOrderFilter('waybillNumber', $event)"
								/>
								<t-switch
									v-model="orderCriteria.inboundEdi"
									:label="$t('inboundEdi')"
									@change="updateInboundEdiFilter"
								/>
								<t-switch
									v-model="orderCriteria.mustHaveDeviation"
									:label="$t('deviation')"
									@change="updateMustHaveDeviation"
								/>
							</div>
						</div>
					</div>
				</div>

				<div>
					<t-checkbox
						v-model="newSettings.mainMenu"
						:label="$t('viewMainMenu')"
						:description="$t('viewMainMenuDesc')"
					/>
					<t-checkbox
						v-model="newSettings.isPublic"
						:label="$t('makePublic')"
					/>
				</div>
			</div>
		</div>
		<template
			v-if="this.$route.params.searchId"
			#additional-buttons
		>
			<t-icon-button
				intent="danger"
				appearance="primary"
				icon="trash"
				type="bp"
				@click="onDeleteSearch"
			/>
		</template>
	</t-dialog>
</template>
<script>
import { mapActions } from './store/saveOrderSearch.store.js';
import { storageDateFormat } from '@/core';
import moment from 'moment';
import deepCopy from 'deepcopy';
import OrderFilters from './OrderFilters';

export default {
	name: 'SaveSearch',
	components: { OrderFilters },
	props: {
		isShown: Boolean,
		searchFilter: Object,
		loadedSearch: Object
	},
	data() {
		return {
			newSettings: {},
			orderTimeInterval: {},
			orderCriteria: {},
			ongoingSave: false,
			missingLabel: false,
		};
	},
	computed: {
		fromDateIsAfterToday: {
			get() {
				return this.orderTimeInterval.before ? (this.orderTimeInterval.before > 0) : false;
			},
			set(toggled) {
				if (toggled) {
					this.orderTimeInterval.before = Math.abs(this.orderTimeInterval.before).toString();
				} else {
					this.orderTimeInterval.before = (Math.abs(this.orderTimeInterval.before) * -1).toString();
				}
			},
		},
		toDateIsAfterToday: {
			get() {
				return this.orderTimeInterval.after ? (this.orderTimeInterval.after > 0) : true;
			},
			set(toggled) {
				if (toggled) {
					this.orderTimeInterval.after = Math.abs(this.orderTimeInterval.after).toString();
				} else {
					this.orderTimeInterval.after = (Math.abs(this.orderTimeInterval.after) * -1).toString();
				}
			},
		},
		before: {
			get() {
				return this.orderTimeInterval.before ? Math.abs(this.orderTimeInterval.before).toString() : '';
			},
			set(value) {
				this.orderTimeInterval.before = (this.fromDateIsAfterToday ? value : value * -1).toString();
			}
		},
		after: {
			get() {
				return this.orderTimeInterval.after ? Math.abs(this.orderTimeInterval.after).toString() : '';
			},
			set(value) {
				if (value) {
					this.orderTimeInterval.after = (this.toDateIsAfterToday ? value : value * -1).toString();
				} else {
					this.orderTimeInterval.after = value;
				}
			}
		},
	},
	methods: {
		...mapActions([
			'addSavedSearch',
			'deleteSavedSearch',
			'updateSavedSearch'
		]),
		processShown() {
			let { date: { from, to } } = this.searchFilter;
			let before = this.diffFromToday(from);
			let after = this.diffFromToday(to);

			this.missingLabel = false;

			if (this.loadedSearch.id === -1) {
				this.newSettings = {
					...deepCopy(this.searchFilter),
					label: '',
					mainMenu: true,
					isPublic: false,
					autoSearch: true,
				};

				this.orderTimeInterval = {
					before,
					after,
					useAfter: Boolean(after),
				};

				this.orderCriteria = {
					fromDespatchParty: Boolean(this.searchFilter.fromDespatchParty.length),
					toDeliveryParty: Boolean(this.searchFilter.toDeliveryParty.length),
					customer: Boolean(this.searchFilter.customer.length),
					goodsTypes: Boolean(this.searchFilter.goodsTypes.length),
					orderTypes: Boolean(this.searchFilter.orderTypes.length),
					hasAtd: this.searchFilter.hasAtd && this.searchFilter.hasAtd.value !== undefined,
					hasAta: this.searchFilter.hasAta && this.searchFilter.hasAta.value !== undefined,
					economicalStatus: Boolean(this.searchFilter.economicalStatus.length),
					orderStatus: Boolean(this.searchFilter.orderStatus.length),
					references: Boolean(this.searchFilter.referencesText.length),
					waybillNumber: Boolean(this.searchFilter.waybillNumber.length),
					inboundEdi: Boolean(this.searchFilter.mustHaveInboundEdi || this.searchFilter.inboundEdi),
					mustHaveDeviation: Boolean(this.searchFilter.mustHaveDeviation),
				};
			} else {
				this.newSettings = {
					...deepCopy(this.searchFilter),
					id: this.loadedSearch.id,
					label: this.loadedSearch.label,
					mainMenu: this.loadedSearch.mainMenu,
					isPublic: this.loadedSearch.isPublic,
					autoSearch: this.loadedSearch.autoSearch,
				};
				this.orderTimeInterval = { ...this.loadedSearch.timeInterval, before, after };
				this.orderTimeInterval.useAfter = Boolean(this.orderTimeInterval.after);

				this.orderCriteria = {
					fromDespatchParty: this.loadedSearch.fromDespatchParty && this.loadedSearch.fromDespatchParty.length > 0,
					toDeliveryParty: this.loadedSearch.toDeliveryParty && this.loadedSearch.toDeliveryParty.length > 0,
					customer: this.loadedSearch.customer && this.loadedSearch.customer.length > 0,
					goodsTypes: this.loadedSearch.goodsTypes && this.loadedSearch.goodsTypes.length > 0,
					orderTypes: this.loadedSearch.orderTypes && this.loadedSearch.orderTypes.length > 0,
					hasAtd: this.loadedSearch.hasAtd && this.loadedSearch.hasAtd.value !== undefined,
					hasAta: this.loadedSearch.hasAta && this.loadedSearch.hasAta.value !== undefined,
					economicalStatus: this.loadedSearch.economicalStatus && this.loadedSearch.economicalStatus.length > 0,
					orderStatus: this.loadedSearch.orderStatus && this.loadedSearch.orderStatus.length > 0,
					references: this.loadedSearch.references && this.loadedSearch.references.length > 0,
					waybillNumber: this.loadedSearch.waybillNumber && this.loadedSearch.waybillNumber.length > 0,
					inboundEdi: Boolean(this.loadedSearch.mustHaveInboundEdi || this.loadedSearch.inboundEdi),
					mustHaveDeviation: Boolean(this.loadedSearch.mustHaveDeviation),
				};
			}
		},
		updateOrderReferences(key, checked) {
			this.updateOrderFilter(key, checked);
			this.newSettings['referencesText'] = checked ? deepCopy(this.searchFilter['referencesText']) : [];
		},
		updateHasStatus(key, checked) {
			this.newSettings[key] = checked ? deepCopy(this.searchFilter[key]) : { value: undefined, label: 'showAny' };
		},
		updateOrderFilter(key, checked, defaultValue = []) {
			this.newSettings[key]
				= checked
					? deepCopy(this.searchFilter[key])
					: defaultValue;
		},
		updateInboundEdiFilter(checked) {

			if (checked) {
				this.newSettings.mustHaveInboundEdi = true;
				this.newSettings.inboundEdi = this.searchFilter.inboundEdi;

			} else {
				this.newSettings.mustHaveInboundEdi = false;
				this.newSettings.inboundEdi = '';
			}
		},
		updateMustHaveDeviation(checked) {

			this.newSettings.mustHaveDeviation = checked;
		},
		diffFromToday(date) {
			if (date)
				return moment(date, storageDateFormat).startOf('day').diff(moment().startOf('day'), 'days').toString();
			return '';
		},
		async onSaveSearch() {
			if (this.ongoingSave) return;
			this.$notify({ clean: true });
			this.ongoingSave = true;

			if (!this.newSettings.label) {
				this.$notify({
					type: 'error',
					title: this.$t('noLabel'),
				});
				this.missingLabel = true;
				this.ongoingSave = false;
				return;
			}
			this.missingLabel = false;

			let { searchId } = this.$route.params;
			try {
				let searchOptions = {
					...this.newSettings,
					timeInterval: { ...this.orderTimeInterval }
				};
				if (searchId) {
					await this.updateSavedSearch({ searchOptions,  id: searchId, searchFilter: this.searchFilter });
				} else {
					let { id } = await this.addSavedSearch({ searchOptions, searchFilter: this.searchFilter });
					this.$router.push({
						name: 'savedOrderSearch',
						params: { searchId: id }
					});
				}
				this.$notify({
					type: 'success',
					title: `${this.$t('savedSearch')}: ${this.newSettings.label}`
				});
				this.$emit('close');
			} catch (e) {
				this.$notify({
					type: 'error',
					duration: -1,
					title: this.$te(e.message) ? this.$t(e.message) : this.$t('failedToSave'),
				});
			}
			this.ongoingSave = false;
		},
		async onDeleteSearch() {
			this.$notify({ clean: true });
			try {
				await this.deleteSavedSearch(this.$route.params.searchId);
				this.$emit('delete');
				this.$notify({
					type: 'success',
					title: `${this.$t('deletedSearch')}: ${this.newSettings.label}`
				});
			} catch (e) {
				this.$notify({
					type: 'error',
					duration: -1,
					title: this.$t('failedToDelete')
				});
			}
		},
		onClose() {
			this.$notify({ clean: true });
			this.$emit('close');
		},
	}
};
</script>

<style lang="scss" scoped>
	@import '~@/components/evergreen/scss/colors';
	@import '~@/components/evergreen/scss/text';

.save-search-top {
	padding-bottom: 20px;
	border-bottom: solid 1px #e4e7eb;
}
.save-search-mid {
	padding-top: 10px;
	display: grid;
	grid-template-columns: 175px auto;
	grid-column-gap: 20px;
}
.save-search-time {
	padding-bottom: 15px;
	border-bottom: solid 1px #e4e7eb;
	display: grid;
	grid-template-rows: repeat(2, auto);
	grid-row-gap: 20px;
}
.time-interval {
	display: grid;
	grid-column-gap: 10px;
	grid-template-columns: auto max-content auto;
	grid-template-areas:
		"input1 checkbox input2"
		"switch1 ....... switch2";
	padding-bottom: 10px;
}

.save-search-criteria {
	padding-top: 20px;
	padding-bottom: 15px;
	border-bottom: solid 1px #e4e7eb;
	display: flex;
	flex-direction: column;
}

.search-criteria-label {
	user-select: none;
	font-size: 11px;
	font-weight: 600;
	line-height: 1.45;
	letter-spacing: 0.3px;
	color: get-color($text, 'muted');
	text-transform: uppercase;
	margin-bottom: 8px;
}
.search-criteria {
	@extend %text-small;
	@extend %font-families-ui;
	font-weight: 600;
	user-select: none;
	line-height: 20px;
	color: get-color($text, 'dark');
	box-sizing: border-box;
	&.disabled {
		color: get-color($text, 'muted');
	}
}
.search-criteria-description {
	@extend %text-small;
	@extend %font-families-ui;
	user-select: none;
	color: get-color($text, 'muted');
	box-sizing: border-box;
	margin-top: 0px;
	margin-bottom: 25px;
}
</style>
